import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { AISystemsService } from 'services/AISystemService';
import {
  AppPrimaryButton,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useNavigate } from 'react-router-dom';
import { DatasetsService } from 'services';
import { Toast } from 'shared/helpers';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';
import useSize from 'shared/hooks/useSize';

interface LinkedDatasetsProps {
  aiSystemId: string;
  datasets: IDataset[];
}

const LinkedDatasets: React.FC<LinkedDatasetsProps> = (props) => {
  const { isMobile } = useSize();
  const { aiSystemId, datasets: tDatasets } = props;
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [linkedDatasets, setLinkedDatasets] = useState(tDatasets);
  const [datasets, setDatasets] = useState<IDataset[]>([]);
  const [showLinkDataset, setShowLinkDataset] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [isLinkingDataset, setIsLinkingDataset] = useState(false);
  const [datesetToUnlink, setDatesetToUnlink] = useState('');
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const datasetsToLink = useMemo(
    () =>
      datasets
        ? datasets.filter((d) => !linkedDatasets.some((ld) => ld._id === d._id))
        : [],
    [datasets, linkedDatasets]
  );

  // API Calls
  const loadDataSets = (): void => {
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    DatasetsService.getDatasets(queryString)
      .then((response) => {
        const { datasets } = response.data.data;
        setDatasets(datasets);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const loadLinkedDatasets = (): void => {
    setIsLoadingDetail(true);
    AISystemsService.getAISystemDetail(`${aiSystemId}`)
      .then((response) => {
        const { data } = response.data;
        setLinkedDatasets(data.datasetId);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingDetail(false);
      });
  };

  // UI Actions
  const actionLinkDataset = (): void => {
    setIsLinkingDataset(true);
    AISystemsService.linkDataset(aiSystemId, {
      action: 'link',
      datasetId: selectedDatasetId,
    })
      .then(() => {
        setIsLinkingDataset(false);
        loadLinkedDatasets();
        setShowLinkDataset(false);
        setSelectedDatasetId('');
      })
      .catch((error) => {
        setIsLinkingDataset(false);
        Toast.showInfoMessage(error.message);
      });
  };

  const actionUnlinkDataset = (dataset: IDataset): void => {
    setDatesetToUnlink(`${dataset._id}`);
    AISystemsService.linkDataset(aiSystemId, {
      action: 'unlink',
      datasetId: dataset._id,
    })
      .then(() => {
        setDatesetToUnlink('');
        loadLinkedDatasets();
      })
      .catch((error) => {
        setDatesetToUnlink('');
        Toast.showInfoMessage(error.message);
      });
  };

  // Hooks
  useEffect(() => {
    if (aiSystemId) loadLinkedDatasets();
    loadDataSets();
  }, [aiSystemId]);
  console.log(isMobile);
  // UI Elements
  return (
    <Box>
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && (
        <Box className="sub-box">
          <Stack
            direction={{ sx: 'column', sm: 'row' }}
            justifyContent="space-between"
            className="mb-4 heading"
            alignItems="center"
            spacing={{ xs: 5, sm: 0 }}
          >
            <Typography className={isMobile ? 'f16 fw-6' : 'f24 fw-7'}>
              Linked Datasets{' '}
            </Typography>
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.aiSystem.aiInventory.view.datasets.addLinkDataset'
            ) && (
              <AppPrimaryButton
                startIcon={<AddIcon />}
                onClick={() => {
                  setShowLinkDataset(true);
                  loadDataSets();
                }}
                className={isMobile ? 'btn-small f14' : 'btn-small'}
                title="Link Dataset"
              />
            )}
          </Stack>

          <Box className="sub-body">
            {linkedDatasets.map((dataset) => (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={{ xs: 2, sm: 0 }}
                sx={{
                  border: '1px solid lightgray',
                  borderRadius: '10px',
                  padding: { xs: '12px', sm: '10px 20px' },
                  margin: '5px 0px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {/* ---- Left / main content ---- */}
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
                  {/* Title + approval chip */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={{ xs: 1, sm: 4 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {dataset.datasetName}
                    </Typography>
                    <Chip
                      size="small"
                      variant="filled"
                      label={dataset.approvalStatus}
                    />
                  </Stack>

                  {/* Meta chips — wrap on mobile */}
                  <Stack
                    direction="row"
                    className="mt-4"
                    spacing={{ xs: 1, sm: 2 }}
                    sx={{
                      flexWrap: 'wrap',
                      rowGap: '6px',
                      mt: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      variant="filled"
                      label={dataset.lifecycleStage}
                    />
                    <Chip
                      size="small"
                      variant="filled"
                      label={dataset.sourceType}
                    />
                    <Chip
                      size="small"
                      variant="filled"
                      label={dataset.sensitiveDataTypesPresent.join(',')}
                      sx={{ maxWidth: '100%' }}
                    />
                  </Stack>

                  {/* Purpose */}
                  <Typography
                    className="mt-4"
                    sx={{
                      mt: 1.5,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {dataset.purposeOfUse}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 1, sm: 2 }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    flexShrink: 0,
                    mt: { xs: 1, sm: 0 },
                  }}
                >
                  {orgACLConfig.aiInventory?.includes(
                    'aiInventory.aiSystem.aiInventory.view.datasets.viewDatasetDetails'
                  ) && (
                    <Button
                      className="btn-small"
                      variant="outlined"
                      startIcon={<LaunchIcon />}
                      onClick={() => {
                        navigate(`/datasets/records/${dataset._id}`, {
                          state: { isFromAIInventory: aiSystemId },
                        });
                      }}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        px: { xs: 1, sm: 2 },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      View Details
                    </Button>
                  )}

                  {datesetToUnlink === dataset._id && (
                    <CircularProgress size={25} />
                  )}

                  {datesetToUnlink === '' &&
                    orgACLConfig.aiInventory?.includes(
                      'aiInventory.aiSystem.aiInventory.view.datasets.deleteDataset'
                    ) && (
                      <IconButton
                        onClick={() => actionUnlinkDataset(dataset)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                </Stack>
              </Stack>
            ))}
            {linkedDatasets.length === 0 && (
              <Box className="empty-content">
                <Typography variant="h3">No dataset linked</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Dialog
        maxWidth="xs"
        fullWidth
        open={showLinkDataset}
        onClose={() => {
          setShowLinkDataset(false);
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3">Link Dataset</Typography>
            <IconButton onClick={() => setShowLinkDataset(false)}>
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <AppSelectWithKeys
            value={selectedDatasetId}
            items={datasetsToLink}
            placeholder="Choose Dataset"
            label="Choose Dataset"
            outerLabel
            titleKey="datasetName"
            valueKey="_id"
            onChange={(e: any) => {
              setSelectedDatasetId(e.target.value);
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 5 }}>
          <AppPrimaryButton
            disabled={!selectedDatasetId}
            className="btn-small"
            title="Link Dataset"
            onClick={actionLinkDataset}
            isLoading={isLinkingDataset}
            loadingText="Linking"
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinkedDatasets;
